"use client";

import { useState } from "react";
import {
  ChartStyleFields,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  Checkbox,
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  OgiveChart,
  type OgiveGuide,
} from "@/components/tools/diagrams-more/ogive-chart";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import { quantile } from "@/lib/stats/descriptive";
import {
  type BinRule,
  buildHistogram,
  MAX_BINS,
} from "@/lib/tool-kit/charts/bins";
import { valueText } from "@/lib/tool-kit/charts/format";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import { parseNumberTokens } from "@/lib/tool-kit/charts/parse";
import { stepDecimals } from "@/lib/tool-kit/charts/scale";
import {
  type CurveReading,
  type OgiveData,
  ogivePoints,
  ogiveRows,
  parseClassTable,
  readCurve,
} from "@/lib/tool-kit/diagrams-more/ogive";

/** English strings. A translated content file overrides these through `ui`. */
export const OGIVE_MAKER_STRINGS = {
  modeLabel: "Your data is",
  modeRaw: "Raw values",
  modeTable: "A class table",
  rawLabel: "Your data — numbers separated by commas, spaces or new lines",
  rawHint:
    "Paste a column from a spreadsheet or type the values. If your numbers use decimal commas, separate them with semicolons.",
  exampleRaw:
    "42, 55, 61, 48, 73, 67, 59, 81, 64, 70, 52, 77, 58, 66, 89, 45, 62, 71, 68, 54, 75, 60, 83, 49, 63, 69, 57, 72, 65, 78, 51, 74, 86, 56, 67, 61, 70, 92, 47, 64",
  tableLabel: "Your class table — one class per line: lower-upper: frequency",
  tableHint:
    "Write 40-50: 12. A dash, an en dash or the word to all work; a colon, semicolon, tab or comma may come before the frequency. List the lowest class first. If your classes have gaps (40-49, 50-59), the boundaries are set halfway between them (49.5, 59.5).",
  exampleTable: "30-40: 3\n40-50: 6\n50-60: 9\n60-70: 12\n70-80: 7\n80-90: 3",
  loadExample: "Load the example",
  exampleTitle: "Cumulative Frequency of Exam Scores",
  exampleXTitle: "Exam score",
  frequencyTitle: "Cumulative frequency",
  percentTitle: "Cumulative percent",
  ruleLabel: "How to group the values into classes",
  ruleSturges: "Sturges' rule",
  ruleScott: "Scott's rule",
  ruleFd: "Freedman–Diaconis rule",
  ruleManual: "Set the class width myself",
  roundWidth: "Round the width to a convenient number",
  widthLabel: "Class width",
  heightLabel: "Vertical axis",
  heightCount: "Cumulative frequency",
  heightPercent: "Cumulative percent",
  guidesLabel: "Read off the curve",
  showQuartiles: "Show Q1, the median and Q3 with guide lines",
  percentileLabel: "Also read off this percentile (optional, 1 to 99)",
  percentilePlaceholder: "e.g. 90",
  labelQ1: "Q1",
  labelMedian: "Median",
  labelQ3: "Q3",
  labelPercentile: "P{p}",
  valuesDetected: "one:{count} value|other:{count} values",
  classesDetected: "one:{count} class|other:{count} classes",
  totalDetected: "n = {count}",
  enterRaw: "Enter at least two numbers to draw an ogive.",
  enterTable: "Enter at least one class, such as 40-50: 12, to draw an ogive.",
  noTotal: "The frequencies add up to 0, so there is nothing to draw.",
  badWidth: "Enter a class width greater than 0.",
  tooManyBins:
    "That width would make more than {max} classes. Choose a wider class.",
  invalidTokens:
    "one:Left out {count} entry that is not a number: {tokens}.|other:Left out {count} entries that are not numbers: {tokens}.",
  adjusted:
    "Your class limits had gaps, so the boundaries were set halfway between them. The table shows the boundaries used.",
  issues: {
    badLine:
      "Line {line}: “{text}” is not in the form lower-upper: frequency, so it was left out.",
    badCount:
      "Line {line}: the frequency in “{text}” must be a number from 0 up, so it was left out.",
    width:
      "Line {line}: the upper limit in “{text}” must be above the lower limit.",
    order:
      "Line {line}: classes must run from lowest to highest, but {text} comes after a higher class.",
    overlap:
      "Line {line}: this class starts below {text}, where the class above it ends, so the classes overlap.",
  },
  readingsHeading: "Values read off the curve",
  measureColumn: "Measure",
  positionColumn: "Height (p × n)",
  classColumn: "Class",
  workingColumn: "L + ((p × n − F) ÷ f) × w",
  curveColumn: "From the curve",
  rawColumn: "From the raw data",
  iqrLabel: "Interquartile range",
  classColumnHead: "Class",
  frequencyColumn: "Frequency",
  cumulativeColumn: "Cumulative frequency",
  percentColumn: "Cumulative %",
  convention:
    "The curve joins (upper boundary, running total) points with straight lines, so each value read off it assumes the values are spread evenly within their class — the grouped-data formula. The raw-data column uses the inclusive quartile method (Excel QUARTILE.INC), which can differ a little from a grouped estimate.",
  labelsAndStyle: "Title and figure style",
  chartTitle: "Title",
  xAxisTitle: "Horizontal axis title",
  yAxisTitle: "Vertical axis title",
  axisTitlePlaceholderX: "e.g. Exam score",
  axisTitlePlaceholderY: "e.g. Cumulative frequency",
  colorLabel: "Color",
  colorBlue: "Blue",
  colorOrange: "Orange",
  colorGreen: "Bluish green",
  colorVermillion: "Vermillion",
  colorSkyBlue: "Sky blue",
  colorPurple: "Reddish purple",
  colorYellow: "Yellow",
  colorBlack: "Black",
  valueLabel: "Label each point with its running total",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and sets the figure number in bold and the title in italics above it — the APA 7 layout.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy table",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  showDataTable: "Show the graph as a table",
  chartLabel: "Ogive",
  chartDescription:
    "Ogive of {total} values in {classes}. The curve rises from 0 at {low} to {total} at {high}.{readings}",
  readingsSentence: " {measure} is about {value}.",
} as const;

type Strings = typeof OGIVE_MAKER_STRINGS;
type Format = ReturnType<typeof useToolNumber>;

interface Built {
  data: OgiveData | null;
  values: number[];
  notices: string[];
  errors: string[];
}

interface Source {
  mode: "raw" | "table";
  raw: string;
  table: string;
  rule: BinRule;
  roundWidth: boolean;
  width: string;
}

function buildFromTable(source: Source, strings: Strings, fmt: Format): Built {
  const parsed = parseClassTable(source.table);
  const notices: string[] = parsed.adjusted ? [strings.adjusted] : [];
  for (const issue of parsed.issues.slice(0, 4)) {
    notices.push(
      t(strings.issues[issue.kind], { line: fmt(issue.line), text: issue.text })
    );
  }
  return { data: parsed.data, values: [], notices, errors: [] };
}

function buildFromRaw(
  source: Source,
  strings: Strings,
  fmt: Format,
  locale: string
): Built {
  const { values, invalid } = parseNumberTokens(source.raw);
  const built: Built = { data: null, values, notices: [], errors: [] };
  if (invalid.length > 0) {
    built.notices.push(
      tPlural(strings.invalidTokens, locale, invalid.length, {
        count: fmt(invalid.length),
        tokens: invalid.slice(0, 5).join(" "),
      })
    );
  }
  if (values.length < 2) {
    return built;
  }
  const hist = buildHistogram(values, {
    rule: source.rule,
    roundWidth: source.roundWidth,
    width: parseNumberInput(source.width),
  });
  if (hist === "badWidth") {
    built.errors.push(strings.badWidth);
  } else if (hist === "tooManyBins") {
    built.errors.push(t(strings.tooManyBins, { max: fmt(MAX_BINS) }));
  } else if (typeof hist !== "string") {
    built.data = {
      edges: hist.edges,
      counts: hist.counts,
      total: hist.counts.reduce((sum, count) => sum + count, 0),
    };
  }
  return built;
}

interface Wanted {
  p: number;
  label: string;
}

function wantedReadings(
  quartiles: boolean,
  extra: string,
  strings: Strings,
  fmt: Format
): Wanted[] {
  const wanted: Wanted[] = quartiles
    ? [
        { p: 0.25, label: strings.labelQ1 },
        { p: 0.5, label: strings.labelMedian },
        { p: 0.75, label: strings.labelQ3 },
      ]
    : [];
  const percent = parseNumberInput(extra);
  const usable = percent !== null && percent > 0 && percent < 100;
  if (usable && !(quartiles && [25, 50, 75].includes(percent))) {
    wanted.push({
      p: percent / 100,
      label: t(strings.labelPercentile, { p: fmt(percent) }),
    });
  }
  return wanted;
}

function DataInputs({
  strings,
  source,
  change,
}: {
  strings: Strings;
  source: Source;
  change: (patch: Partial<Source>) => void;
}) {
  const raw = source.mode === "raw";
  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name="ogm-mode"
        onChange={(mode) => change({ mode })}
        options={[
          { value: "raw", label: strings.modeRaw },
          { value: "table", label: strings.modeTable },
        ]}
        value={source.mode}
      />
      <TextAreaField
        hint={raw ? strings.rawHint : strings.tableHint}
        id={raw ? "ogm-raw" : "ogm-table"}
        label={raw ? strings.rawLabel : strings.tableLabel}
        onChange={(text) => change(raw ? { raw: text } : { table: text })}
        rows={raw ? 5 : 7}
        value={raw ? source.raw : source.table}
      />
      {raw ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="ogm-rule"
            label={strings.ruleLabel}
            onChange={(rule) => change({ rule })}
            options={[
              { value: "sturges", label: strings.ruleSturges },
              { value: "scott", label: strings.ruleScott },
              { value: "freedman-diaconis", label: strings.ruleFd },
              { value: "manual", label: strings.ruleManual },
            ]}
            value={source.rule}
          />
          {source.rule === "manual" ? (
            <TextField
              id="ogm-width"
              label={strings.widthLabel}
              numeric
              onChange={(width) => change({ width })}
              value={source.width}
            />
          ) : (
            <div className="self-end">
              <Checkbox
                checked={source.roundWidth}
                label={strings.roundWidth}
                onChange={(roundWidth) => change({ roundWidth })}
              />
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function Status({
  strings,
  built,
  data,
  mode,
}: {
  strings: Strings;
  built: Built;
  data: OgiveData | null;
  mode: "raw" | "table";
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const empty = !data && built.errors.length === 0 && built.data === null;
  return (
    <div aria-live="polite" className="space-y-2">
      {data ? (
        <p className={HINT}>
          {t(strings.totalDetected, { count: fmt(data.total) })}
          {" · "}
          {tPlural(strings.classesDetected, locale, data.counts.length, {
            count: fmt(data.counts.length),
          })}
        </p>
      ) : null}
      {empty ? (
        <Notice>
          {mode === "raw" ? strings.enterRaw : strings.enterTable}
        </Notice>
      ) : null}
      {built.data && !data ? <Notice>{strings.noTotal}</Notice> : null}
      {built.errors.map((message) => (
        <ErrorNotice key={message}>{message}</ErrorNotice>
      ))}
      {built.notices.map((message) => (
        <Notice key={message}>{message}</Notice>
      ))}
    </div>
  );
}

interface Reading extends Wanted {
  reading: CurveReading;
}

function Readings({
  strings,
  readings,
  data,
  values,
  edgeText,
}: {
  strings: Strings;
  readings: Reading[];
  data: OgiveData;
  /** Raw values, for the exact-quartile column; empty for a class table. */
  values: number[];
  edgeText: (value: number) => string;
}) {
  const fmt = useToolNumber();
  const number = (value: number) => valueText(fmt, value, 2);
  const withRaw = values.length >= 2;
  const head = [
    strings.measureColumn,
    strings.positionColumn,
    strings.classColumn,
    strings.workingColumn,
    strings.curveColumn,
    ...(withRaw ? [strings.rawColumn] : []),
  ];
  const rows = readings.map(({ label, p, reading }) => [
    label,
    number(reading.target),
    `${edgeText(data.edges[reading.classIndex])} – ${edgeText(data.edges[reading.classIndex + 1])}`,
    `${number(reading.lower)} + ((${number(reading.target)} − ${number(reading.before)}) ÷ ${number(reading.frequency)}) × ${number(reading.width)}`,
    number(reading.value),
    ...(withRaw ? [number(quantile(values, p, "inclusive"))] : []),
  ]);
  const byLabel = new Map(
    readings.map((entry) => [entry.label, entry.reading])
  );
  const q1 = byLabel.get(strings.labelQ1);
  const q3 = byLabel.get(strings.labelQ3);
  const median = byLabel.get(strings.labelMedian);
  return (
    <div className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.readingsHeading}</h2>
      {q1 && q3 && median ? (
        <StatGrid>
          <StatCard label={strings.labelQ1} value={number(q1.value)} />
          <StatCard label={strings.labelMedian} value={number(median.value)} />
          <StatCard label={strings.labelQ3} value={number(q3.value)} />
          <StatCard
            label={strings.iqrLabel}
            value={number(q3.value - q1.value)}
          />
        </StatGrid>
      ) : null}
      <DataTable head={head} rows={rows} />
      <p className={HINT}>{strings.convention}</p>
    </div>
  );
}

export default function OgiveMakerWidget() {
  const strings = useToolUi(OGIVE_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [source, setSource] = useState<Source>({
    mode: "raw",
    raw: strings.exampleRaw,
    table: strings.exampleTable,
    rule: "sturges",
    roundWidth: true,
    width: "10",
  });
  const [percent, setPercent] = useState(false);
  const [quartiles, setQuartiles] = useState(true);
  const [extra, setExtra] = useState("");
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: strings.exampleXTitle,
    yTitle: strings.frequencyTitle,
    color: "blue",
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  });
  const change = (patch: Partial<Source>) =>
    setSource((old) => ({ ...old, ...patch }));

  const built =
    source.mode === "table"
      ? buildFromTable(source, strings, fmt)
      : buildFromRaw(source, strings, fmt, locale);
  const data = built.data && built.data.total > 0 ? built.data : null;
  const number = (value: number) => valueText(fmt, value, 2);
  const readings: Reading[] = data
    ? wantedReadings(quartiles, extra, strings, fmt).flatMap((entry) => {
        const reading = readCurve(data, entry.p);
        return reading ? [{ ...entry, reading }] : [];
      })
    : [];
  const yScale = percent ? 100 / (data?.total ?? 1) : 1;
  const guides: OgiveGuide[] = readings.map(({ label, reading }) => ({
    label: `${label} = ${number(reading.value)}`,
    x: reading.value,
    y: reading.target * yScale,
  }));
  const yTitle =
    percent && style.yTitle === strings.frequencyTitle
      ? strings.percentTitle
      : style.yTitle;

  function changeHeight(next: "count" | "percent") {
    setPercent(next === "percent");
    const from =
      next === "percent" ? strings.frequencyTitle : strings.percentTitle;
    if (style.yTitle === from) {
      update({
        yTitle:
          next === "percent" ? strings.percentTitle : strings.frequencyTitle,
      });
    }
  }

  const edgeDecimals = data
    ? Math.min(
        4,
        Math.max(...data.edges.map((edge) => stepDecimals(Math.abs(edge))))
      )
    : 0;
  const edgeText = (value: number) =>
    fmt(value, {
      minimumFractionDigits: edgeDecimals,
      maximumFractionDigits: edgeDecimals,
    });
  const classRows = data
    ? ogiveRows(data).map((row) => [
        `${edgeText(row.from)} – ${edgeText(row.to)}`,
        fmt(row.frequency),
        fmt(row.cumulative),
        valueText(fmt, row.percent, 1),
      ])
    : [];
  const classHead = [
    strings.classColumnHead,
    strings.frequencyColumn,
    strings.cumulativeColumn,
    strings.percentColumn,
  ];

  return (
    <div className="space-y-6">
      <DataInputs change={change} source={source} strings={strings} />
      <Status built={built} data={data} mode={source.mode} strings={strings} />
      <div className="grid gap-6 sm:grid-cols-2">
        <PillGroup
          label={strings.heightLabel}
          name="ogm-height"
          onChange={changeHeight}
          options={[
            { value: "count", label: strings.heightCount },
            { value: "percent", label: strings.heightPercent },
          ]}
          value={percent ? "percent" : "count"}
        />
        <div className="space-y-3">
          <p className="font-medium text-foreground text-sm">
            {strings.guidesLabel}
          </p>
          <Checkbox
            checked={quartiles}
            label={strings.showQuartiles}
            onChange={setQuartiles}
          />
          <TextField
            id="ogm-percentile"
            label={strings.percentileLabel}
            numeric
            onChange={setExtra}
            placeholder={strings.percentilePlaceholder}
            value={extra}
          />
        </div>
      </div>
      {data ? (
        <ChartFigure
          apa={style.apa}
          data={toTsv([classHead, ...classRows])}
          figureNumber={style.figureNumber}
          filename={chartFilename(style.title, "ogive")}
          labels={strings}
          note={style.note}
          table={{ head: classHead, rows: classRows }}
          title={style.title}
        >
          <OgiveChart
            description={t(strings.chartDescription, {
              total: fmt(data.total),
              classes: tPlural(
                strings.classesDetected,
                locale,
                data.counts.length,
                { count: fmt(data.counts.length) }
              ),
              low: edgeText(data.edges[0]),
              high: edgeText(data.edges.at(-1) ?? 0),
              readings: readings
                .map(({ label, reading }) =>
                  t(strings.readingsSentence, {
                    measure: label,
                    value: number(reading.value),
                  })
                )
                .join(""),
            })}
            edgeDecimals={edgeDecimals}
            edges={data.edges}
            fmt={fmt}
            guides={guides}
            id="ogm-chart"
            label={style.title.trim() || strings.chartLabel}
            percent={percent}
            points={ogivePoints(data).map(([x, y]) => [x, y * yScale] as const)}
            style={{ ...style, yTitle }}
            yMax={percent ? 100 : data.total}
          />
        </ChartFigure>
      ) : null}
      {data && readings.length > 0 ? (
        <Readings
          data={data}
          edgeText={edgeText}
          readings={readings}
          strings={strings}
          values={built.values}
        />
      ) : null}
      <ChartStyleFields
        axisLabels={{
          xAxisTitle: strings.xAxisTitle,
          yAxisTitle: strings.yAxisTitle,
          axisTitlePlaceholderX: strings.axisTitlePlaceholderX,
          axisTitlePlaceholderY: strings.axisTitlePlaceholderY,
        }}
        colorLabels={strings}
        idPrefix="ogm"
        labels={strings}
        style={style}
        update={update}
        valueLabel={strings.valueLabel}
      />
    </div>
  );
}
